import { useState, useEffect, useCallback } from 'react';
import { healthAPI } from '@/lib/api';
import { APP_VERSION } from '@/lib/version';
import {
  FileText, FolderOpen, Tag, FileCode, Bell, Paperclip, Lock, Unlock,
  HardDrive, Shield, Clock, AlertTriangle, Download, RefreshCw, Activity,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-muted-foreground', testId }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border/40" data-testid={testId}>
      <div className={`p-2 rounded-md bg-background/50 ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-semibold text-foreground">{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground/70">{sub}</p>}
      </div>
    </div>
  );
}

export default function HealthDashboard() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await healthAPI.get();
      setHealth(res.data);
    } catch {
      toast.error('Failed to load health data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const exportDiagnostics = () => {
    if (!health) return;
    const blob = new Blob([JSON.stringify(health, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vaultnotes-diagnostics-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Diagnostics exported');
  };

  if (loading || !health) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="health-loading">
        <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { counts, storage, encryption, backup } = health;
  const backupAge = backup.last_backup_at
    ? formatDistanceToNow(new Date(backup.last_backup_at), { addSuffix: true })
    : null;
  const backupStale = backup.last_backup_at
    ? (Date.now() - new Date(backup.last_backup_at).getTime()) > 7 * 24 * 60 * 60 * 1000
    : true;

  return (
    <div className="space-y-5" data-testid="health-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-accent-cyan" />
          <h3 className="text-sm font-semibold">System Health</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchHealth} className="h-7 px-2" data-testid="health-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Backup Status Banner */}
      <div className={`p-3 rounded-lg border flex items-start gap-3 ${
        backupStale
          ? 'bg-amber-500/5 border-amber-500/30'
          : 'bg-green-500/5 border-green-500/30'
      }`} data-testid="health-backup-status">
        {backupStale ? (
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
        ) : (
          <Shield className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
        )}
        <div>
          <p className={`text-xs font-medium ${backupStale ? 'text-amber-500' : 'text-green-500'}`}>
            {backupStale ? 'Backup recommended' : 'Backup up to date'}
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {backupAge ? `Last backup ${backupAge}` : 'No backups recorded yet'}
            {!backup.has_recovery_key && ' — Recovery key not set'}
          </p>
        </div>
      </div>

      {/* Data Counts */}
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">Data</p>
        <div className="grid grid-cols-2 gap-2">
          <StatCard icon={FileText} label="Notes" value={counts.notes} sub={counts.deleted_notes > 0 ? `${counts.deleted_notes} in trash` : null} color="text-accent-amber" testId="health-notes" />
          <StatCard icon={FolderOpen} label="Notebooks" value={counts.notebooks} color="text-accent-cyan" testId="health-notebooks" />
          <StatCard icon={Tag} label="Tags" value={counts.tags} color="text-purple-400" testId="health-tags" />
          <StatCard icon={FileCode} label="Templates" value={counts.templates} color="text-blue-400" testId="health-templates" />
          <StatCard icon={Bell} label="Reminders" value={`${counts.reminders_pending} pending`} sub={`${counts.reminders_total} total`} color="text-orange-400" testId="health-reminders" />
          <StatCard icon={Paperclip} label="Attachments" value={counts.attachments} color="text-teal-400" testId="health-attachments" />
          <StatCard icon={Clock} label="Versions" value={counts.versions} sub="Note edit history" color="text-muted-foreground" testId="health-versions" />
        </div>
      </div>

      {/* Storage */}
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">Storage</p>
        <div className="p-3 rounded-lg bg-secondary/30 border border-border/40" data-testid="health-storage">
          <div className="flex items-center gap-3 mb-2">
            <HardDrive className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-semibold">{formatBytes(storage.total_bytes)}</span>
            <span className="text-xs text-muted-foreground">total</span>
          </div>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span>Notes: {formatBytes(storage.notes_bytes)}</span>
            <span>Attachments: {formatBytes(storage.attachments_bytes)}</span>
          </div>
        </div>
      </div>

      {/* Encryption */}
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">Encryption</p>
        <div className="p-3 rounded-lg bg-secondary/30 border border-border/40" data-testid="health-encryption">
          <div className="flex items-center gap-3 mb-2">
            {encryption.enabled ? (
              <Lock className="w-4 h-4 text-accent-amber" />
            ) : (
              <Unlock className="w-4 h-4 text-muted-foreground" />
            )}
            <span className="text-sm font-semibold">
              {encryption.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          {encryption.enabled && counts.notes > 0 && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{encryption.encrypted_notes} encrypted</span>
                <span>{encryption.coverage_pct}% coverage</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent-amber rounded-full transition-all"
                  style={{ width: `${encryption.coverage_pct}%` }}
                />
              </div>
              {encryption.unencrypted_notes > 0 && (
                <p className="text-[10px] text-muted-foreground/70">
                  {encryption.unencrypted_notes} note{encryption.unencrypted_notes !== 1 ? 's' : ''} unencrypted
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Diagnostics */}
      <Button variant="outline" size="sm" className="w-full" onClick={exportDiagnostics} data-testid="health-export-diagnostics">
        <Download className="w-3.5 h-3.5 mr-1.5" /> Export Diagnostics
      </Button>

      {/* Version */}
      <p className="text-center text-[10px] text-muted-foreground/40 pt-1" data-testid="health-app-version">
        VaultNotes v{APP_VERSION}
      </p>
    </div>
  );
}
